import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  FiClock,
  FiChevronRight,
  FiTrash2,
  FiSearch,
  FiCompass,
  FiBookmark,
  FiEye,
  FiPhoneCall,
} from "react-icons/fi";
import { getShortlistCount } from "../../utils/shortlistUtils";
import "./RecentelySearched.css";

const INITIAL_SEARCHES = [
  {
    id: "rs-1",
    group: "Today - Saturday (16 Sept 2023)",
    type: "buy",
    title: "Buy in Hyderabad, Independent/Builder Floor",
    location: "Hyderabad",
    propType: "Independent/Builder Floor",
    time: "2 hours ago",
    query: "/allproperty?type=buy&city=Hyderabad",
  },
  {
    id: "rs-2",
    group: "Tuesday (12 Sept 2023)",
    type: "buy",
    title: "Buy in Hyderabad, Residential Apartment",
    location: "Hyderabad",
    propType: "Residential Apartment",
    time: "3 days ago",
    query: "/allproperty?type=buy&city=Hyderabad",
  },
  {
    id: "rs-3",
    group: "Friday (08 Sept 2023)",
    type: "rent",
    title: "Rent in Kondapur, Hyderabad",
    location: "Kondapur, Hyderabad",
    propType: "Rental Home",
    time: "1 week ago",
    query: "/allproperty?type=rent&city=Hyderabad",
  },
  {
    id: "rs-4",
    group: "Friday (08 Sept 2023)",
    type: "buy",
    title: "Buy in Hyderabad, Independent House/Villa",
    location: "Hyderabad",
    propType: "Villa",
    time: "1 week ago",
    query: "/allproperty?type=buy&city=Hyderabad",
  },
  {
    id: "rs-5",
    group: "Friday (08 Sept 2023)",
    type: "buy",
    title: "Buy in Hyderabad, Residential Apartment",
    location: "Hyderabad",
    propType: "Residential Apartment",
    time: "1 week ago",
    query: "/allproperty?type=buy&city=Hyderabad",
  },
  {
    id: "rs-6",
    group: "Friday (08 Sept 2023)",
    type: "buy",
    title: "Buy in Delhi / NCR, Residential Land",
    location: "Delhi / NCR",
    propType: "Residential Land",
    time: "1 week ago",
    query: "/allproperty?type=buy&city=Delhi",
  },
  {
    id: "rs-7",
    group: "Wednesday (06 Sept 2023)",
    type: "projects",
    title: "Buy Projects in, Hyderabad, Independent/Builder Floor",
    location: "Hyderabad",
    propType: "Builder Floor Projects",
    time: "10 days ago",
    query: "/allproperty?type=buy&city=Hyderabad",
  },
  {
    id: "rs-8",
    group: "Wednesday (06 Sept 2023)",
    type: "projects",
    title: "Buy Projects in, Hyderabad, Independent/Builder Floor + 1 more",
    location: "Hyderabad",
    propType: "Projects",
    time: "10 days ago",
    query: "/allproperty?type=buy&city=Hyderabad",
  },
  {
    id: "rs-9",
    group: "Wednesday (06 Sept 2023)",
    type: "projects",
    title: "Buy Projects in, Hyderabad, Independent/Builder Floor + 1 more",
    location: "Hyderabad",
    propType: "Projects",
    time: "10 days ago",
    query: "/allproperty?type=buy&city=Hyderabad",
  },
  {
    id: "rs-10",
    group: "Wednesday (06 Sept 2023)",
    type: "projects",
    title: "Buy Projects in, Hyderabad, Independent/Builder Floor + 1 more",
    location: "Hyderabad",
    propType: "Projects",
    time: "10 days ago",
    query: "/allproperty?type=buy&city=Hyderabad",
  },
  {
    id: "rs-11",
    group: "Wednesday (06 Sept 2023)",
    type: "buy",
    title: "Buy in Chennai",
    location: "Chennai",
    propType: "All Properties",
    time: "12 days ago",
    query: "/allproperty?type=buy&city=Chennai",
  },
];

const RecentelySearched = () => {
  const navigate = useNavigate();
  const [searches, setSearches] = useState(() => {
    try {
      const saved = localStorage.getItem("mgh_recent_searches");
      return saved ? JSON.parse(saved) : INITIAL_SEARCHES;
    } catch (e) {
      return INITIAL_SEARCHES;
    }
  });

  const [shortlistCount, setShortlistCount] = useState(0);

  useEffect(() => {
    setShortlistCount(getShortlistCount());
    const handleUpdate = () => setShortlistCount(getShortlistCount());
    window.addEventListener("mgh_shortlist_updated", handleUpdate);
    return () => window.removeEventListener("mgh_shortlist_updated", handleUpdate);
  }, []);

  const handleClearAll = () => {
    if (window.confirm("Are you sure you want to clear your recent searches?")) {
      setSearches([]);
      try {
        localStorage.setItem("mgh_recent_searches", JSON.stringify([]));
      } catch (e) {
        // ignore
      }
    }
  };

  const handleDeleteItem = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const updated = searches.filter((item) => item.id !== id);
    setSearches(updated);
    try {
      localStorage.setItem("mgh_recent_searches", JSON.stringify(updated));
    } catch (err) {
      // ignore
    }
  };

  const handleRecentSearchClick = (item) => {
    const path = typeof item?.query === "string" ? item.query.trim() : "";
    const safePath = path && path.startsWith("/") ? path : "/allproperty";
    navigate(safePath.includes("?") ? "/allproperty" : safePath || "/allproperty");
  };

  // Group items by date header
  const groupedSearches = searches.reduce((acc, curr) => {
    const key = curr.group || "Earlier";
    if (!acc[key]) acc[key] = [];
    acc[key].push(curr);
    return acc;
  }, {});

  return (
    <div className="mgh-recent-page-wrapper">
      <Helmet>
        <title>Recently Searched Properties | My Golden Homes</title>
        <meta
          name="description"
          content="Review your recent search history, queries, and locations on My Golden Homes."
        />
      </Helmet>

      <div className="mgh-recent-container">
        {/* Breadcrumb */}
        <div className="mgh-recent-breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <span>Recent Searches</span>
        </div>

        {/* Activity Tabs */}
        <div className="mgh-recent-activity-tabs">
          <Link to="/recentelysearched" className="mgh-recent-tab-item active">
            <FiClock />
            <span>Recent Searches</span>
            <span className="mgh-recent-tab-badge">{searches.length}</span>
          </Link>
          <Link to="/viewed" className="mgh-recent-tab-item">
            <FiEye />
            <span>Recently Viewed</span>
            <span className="mgh-recent-tab-badge">11</span>
          </Link>
          <Link to="/shortlisted" className="mgh-recent-tab-item">
            <FiBookmark />
            <span>Shortlisted</span>
            <span className="mgh-recent-tab-badge">{shortlistCount}</span>
          </Link>
          <Link to="/contacted" className="mgh-recent-tab-item">
            <FiPhoneCall />
            <span>Contacted</span>
            <span className="mgh-recent-tab-badge">0</span>
          </Link>
        </div>

        {/* Header Summary Card */}
        <div className="mgh-recent-header-card">
          <div>
            <h1 className="mgh-recent-header-title">Recent Searches</h1>
            <p className="mgh-recent-header-subtitle">
              Pick up where you left off. Revisit your past property search queries with one click.
            </p>
          </div>
          <div className="mgh-recent-header-actions">
            {searches.length > 0 && (
              <button
                type="button"
                className="mgh-recent-clear-btn"
                onClick={handleClearAll}
              >
                <FiTrash2 /> Clear All
              </button>
            )}
            <Link to="/allproperty" className="mgh-recent-explore-btn">
              <FiSearch /> New Search
            </Link>
          </div>
        </div>

        {/* Search Items or Empty State */}
        {searches.length === 0 ? (
          <div className="mgh-recent-empty">
            <div className="mgh-recent-empty-icon">
              <FiSearch />
            </div>
            <h3>No Recent Searches</h3>
            <p>
              You haven&rsquo;t performed any recent property searches yet. Explore thousands of verified apartments, villas, and plots across India.
            </p>
            <Link to="/allproperty" className="mgh-recent-explore-btn">
              <FiCompass /> Explore Properties
            </Link>
          </div>
        ) : (
          Object.entries(groupedSearches).map(([groupTitle, items]) => (
            <div key={groupTitle} className="mgh-recent-date-group">
              <div className="mgh-recent-date-heading">
                <span className="dot"></span>
                <span>{groupTitle}</span>
              </div>
              <div className="mgh-recent-list">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="mgh-recent-item-card"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRecentSearchClick(item)}
                  >
                    <div className="mgh-recent-item-left">
                      <div className="mgh-recent-item-icon">
                        <FiClock />
                      </div>
                      <div className="mgh-recent-item-info">
                        <div className="mgh-recent-item-tags">
                          <span
                            className={`mgh-recent-type-badge ${
                              item.type === "rent"
                                ? "rent"
                                : item.type === "projects"
                                ? "projects"
                                : "buy"
                            }`}
                          >
                            {item.type || "BUY"}
                          </span>
                          <span className="mgh-recent-item-meta">{item.time}</span>
                        </div>
                        <h4 className="mgh-recent-item-title">{item.title}</h4>
                      </div>
                    </div>
                    <div className="mgh-recent-item-right">
                      <button
                        type="button"
                        className="mgh-recent-item-delete"
                        title="Remove from history"
                        onClick={(e) => handleDeleteItem(e, item.id)}
                      >
                        <FiTrash2 />
                      </button>
                      <div className="mgh-recent-item-arrow">
                        <FiChevronRight />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentelySearched;